import React from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheck, Users, Network } from 'lucide-react'

export default function Home() {
  return (
    <>
      <section className="hero-wrap">
        <div className="hero">
          <div className="hero-content">
            <div className="badge-pill"><Network size={16} /> Behavioral Health Infrastructure</div>
            <h1 className="hero-headline">
              <span>Routing and Continuity Infrastructure</span>
              <span className="hero-divider" aria-hidden="true" />
              <span>for Outpatient Behavioral Health</span>
            </h1>
            <p className="sub">
              ATUUNE helps health plans and provider organizations improve behavioral-health routing at
              intake and identify patients at risk of disengaging from care — working underneath the
              systems they already use.
            </p>
            <div className="actions">
              <Link to="/contact">Request Demo</Link>
              <Link className="secondary" to="/how-it-works">See How It Works</Link>
            </div>
            <p className="disclaimer">Not for emergencies. ATUUNE does not provide medical advice or therapy.</p>
          </div>
        </div>
      </section>

      <section className="section light">
        <h2>Who We Serve</h2>
        <p className="lead muted">
          Solo providers and practices remain fully supported — but the primary story is infrastructure
          for the organizations responsible for routing and continuity at scale.
        </p>
        <div className="cards">
          <div className="card">
            <ShieldCheck />
            <h3>Health Plans</h3>
            <p>Improve routing, strengthen continuity, and identify members at risk of disengagement before care breaks down.</p>
          </div>
          <div className="card">
            <Network />
            <h3>Provider Organizations</h3>
            <p>Route patients more effectively, support care teams with continuity alerts, and improve operational visibility.</p>
          </div>
          <div className="card">
            <Users />
            <h3>Patients</h3>
            <p>Complete a structured intake and be routed to providers who better fit your clinical and practical needs.</p>
          </div>
        </div>
      </section>

      <section className="section light">
        <h2>Structured Intake &amp; Care Routing</h2>
        <p className="lead muted">
          Structured intake creates a richer routing signal by combining clinical needs, care preferences,
          logistics, and provider attributes to support more informed routing decisions.
        </p>
      </section>

      <section className="section light">
        <h2>Why Existing Systems Aren't Enough</h2>
        <p className="lead muted">
          Referral and scheduling systems answer the first question. They rarely answer the second.
        </p>
        <div className="comparison-columns">
          <div className="comparison-column have">
            <h3>Current systems know</h3>
            <ul>
              <li>✓ Referral made</li>
              <li>✓ Appointment booked</li>
            </ul>
          </div>
          <div className="comparison-column gap">
            <h3>Current systems often cannot see</h3>
            <ul>
              <li>• Patients drifting away</li>
              <li>• Early continuity risk</li>
              <li>• Re-engagement opportunities</li>
            </ul>
            <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>ATUUNE fills this operational gap.</p>
          </div>
        </div>
      </section>

      <section className="section light">
        <h2>Two Capabilities, One Infrastructure Layer</h2>
        <p className="lead muted">
          Route patients into the right care, then keep visibility on whether that care is holding.
        </p>
        <div className="flow-columns">
          <div className="flow-column">
            <h3>Route for Better Fit</h3>
            <p className="muted">Help route patients into existing provider networks using richer intake information rather than availability alone.</p>
          </div>
          <div className="flow-column">
            <h3>Improve Early Continuity</h3>
            <p className="muted">Identify when patients may be drifting away from care so providers can intervene earlier.</p>
          </div>
        </div>
        <div className="actions" style={{ marginTop: 24 }}>
          <Link className="secondary outline-dark" to="/platform">See the Full Platform</Link>
        </div>
      </section>

      <section className="section light">
        <h2>See It Working Underneath Your Systems</h2>
        <p className="lead muted">
          Request a demo scoped to your existing provider network — no rebuild, no workflow replacement.
        </p>
        <div className="actions">
          <Link to="/contact">Request Demo</Link>
          <Link className="secondary outline-dark" to="/how-it-works">See How It Works</Link>
        </div>
      </section>
    </>
  )
}
